import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  Paper,
} from "@mui/material";
import { styled, useTheme } from "@mui/system";
import { keyframes } from "@emotion/react";

// Placeholder Icons for Sauna Features (Using standard elegant icons)
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // Quality/Exclusive
import LockOpenIcon from '@mui/icons-material/LockOpen'; // Private Access
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'; // App Booking
import StarOutlineIcon from '@mui/icons-material/StarOutline'; // Premium Experience

// Keyframe animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { transform: scale(0.98); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

// --- Thematic Constants (Elegant & Minimalist Palette) ---
const darkGraphite = '#212121'; // Primary - Deep Charcoal/Black
const softBeige = '#f5f5f5'; // Background - Light and clean
const goldAccent = '#b89f50'; // Secondary - Luxurious Gold/Brass
const offWhite = '#ffffff'; // Paper/Card Background

// --- Styled Components ---

const MainContainer = styled(Box)(({ theme }) => ({
  background: softBeige,
  color: darkGraphite,
  minHeight: "100vh",
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(10),
  fontFamily: "'Raleway', sans-serif", // Clean, elegant, and minimalist font
  animation: `${fadeIn} 0.8s ease-out`,
}));

const Hero = styled(Box)(({ theme }) => ({
  background: darkGraphite, // Dark, high-contrast header
  color: offWhite,
  padding: theme.spacing(12, 2),
  textAlign: "center",
  borderRadius: theme.shape.borderRadius * 0.5, // Less rounding for a sharper, modern look
  marginBottom: theme.spacing(10),
  boxShadow: theme.shadows[10], // Deep shadow for a sense of quality
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 300, // Thinner font weight for elegance
    letterSpacing: '5px', // More space between letters
    textTransform: 'uppercase',
    marginBottom: theme.spacing(2),
}));

const HeroSubtitle = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    color: goldAccent, // Highlighted gold accent
    letterSpacing: '2px',
    marginBottom: theme.spacing(5),
}));

const Section = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(12),
  marginBottom: theme.spacing(12),
}));

const CTAButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(5),
  background: goldAccent, // Exclusive Gold CTA
  color: darkGraphite,
  padding: theme.spacing(1.5, 6),
  fontSize: "1.25rem",
  borderRadius: theme.shape.borderRadius * 0.5,
  textTransform: "uppercase",
  fontWeight: 600,
  transition: "all 0.3s ease-in-out",
  border: `2px solid ${goldAccent}`,
  boxShadow: `0 4px 10px rgba(184, 159, 80, 0.4)`,
  "&:hover": {
    background: darkGraphite,
    color: goldAccent,
    transform: "translateY(-1px)",
    boxShadow: `0 6px 15px rgba(184, 159, 80, 0.7)`,
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 0.5,
  height: "100%",
  background: offWhite,
  boxShadow: theme.shadows[1], // Very light shadow
  transition: "all 0.4s ease-in-out",
  borderBottom: `3px solid ${goldAccent}`, // Subtle gold underline
  "&:hover": {
    boxShadow: theme.shadows[4],
    transform: "translateY(-2px)",
  },
  display: 'flex',
  flexDirection: 'column',
}));

const CardIcon = styled(Box)(({ theme }) => ({
  fontSize: "3rem",
  marginBottom: theme.spacing(2),
  textAlign: "center",
  color: goldAccent, // Gold icons
}));

const SaunaHavenLanding = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Apply custom palette for the component
  React.useEffect(() => {
    theme.palette.primary.main = darkGraphite;
    theme.palette.secondary.main = goldAccent;
    theme.palette.text.primary = darkGraphite;
    theme.palette.background.default = softBeige;
    theme.palette.background.paper = offWhite;
  }, [theme]);


  return (
    <MainContainer>
      <Container maxWidth="lg">
        <Hero id="hero">
          <HeroTitle
            variant={isMobile ? "h4" : "h3"}
            sx={{ animation: `${fadeIn} 1s ease-out` }}
          >
            Presentamos
          </HeroTitle>
          <Typography
            variant={isMobile ? "h2" : "h1"}
            fontWeight="bold"
            gutterBottom
            sx={{ animation: `${fadeIn} 1s ease-out` }}
          >
            SAUNA HAVEN
          </Typography>
          <HeroSubtitle
            variant={isMobile ? "h6" : "h5"}
            sx={{ animation: `${fadeIn} 1.2s ease-out` }}
          >
            Exclusividad y Bienestar Privado en un Solo Lugar.
          </HeroSubtitle>
            
          {/* Placeholder for a high-end sauna image */}
          <Box
            component="img"
            src="https://s.alicdn.com/@sc04/kf/H37264bff9e95475fa88e0802e990216dH.jpg_321x321.jpg"
            alt="Interior de sauna de lujo con diseño minimalista"
            sx={{
              width: "100%",
              maxWidth: "1000px",
              borderRadius: theme.shape.borderRadius * 0.5,
              margin: '40px auto',
              maxHeight: isMobile ? "300px" : "500px",
              objectFit: "cover",
              boxShadow: theme.shadows[10],
              display: 'block',
              border: `1px solid ${goldAccent}`,
            }}
          />

          <CTAButton variant="contained" sx={{ animation: `${fadeIn} 1.4s ease-out` }}>
            Solicitar Acceso Privado
          </CTAButton>
        </Hero>
      </Container>

      <Container maxWidth="md">
        <Section>
          <Typography
            variant="h4"
            gutterBottom
            fontWeight="light"
            align="center"
            color={darkGraphite}
            mb={2}
            textTransform="uppercase"
          >
            Sistema de Reserva Exclusivo
          </Typography>
          <Typography
            variant="h2"
            gutterBottom
            fontWeight="bold"
            align="center"
            color={goldAccent}
            mb={6}
          >
            Reserva desde Casa. Accede sin Contacto.
          </Typography>

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              {/* Placeholder for an image of the app booking screen */}
              <Box
                  component="img"
                  src="https://www.smarttravel.news/wp-content/uploads/2022/09/LoungeUp_Email-de-pre-check-in-online-enviado-en-pre-estancia.png"
                  alt="Pantalla de la aplicación de reservas elegante"
                  sx={{
                      width: "100%",
                      maxWidth: "350px",
                      borderRadius: theme.shape.borderRadius * 1,
                      boxShadow: theme.shadows[8],
                      display: 'block',
                      margin: '0 auto',
                      border: `5px solid ${darkGraphite}`,
                  }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, textAlign: isMobile ? 'center' : 'left' }}>
                <PhoneIphoneIcon sx={{ fontSize: '4rem', color: goldAccent, mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  The Haven App
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  Nuestra aplicación privada le permite gestionar su tiempo de bienestar. Consulte la disponibilidad en tiempo real y confirme su sesión desde cualquier lugar con la **máxima discreción**.
                </Typography>
                <Box mt={3} textAlign={isMobile ? 'center' : 'left'}>
                    <Button variant="outlined" sx={{ color: darkGraphite, borderColor: darkGraphite, '&:hover': {borderColor: goldAccent, color: goldAccent} }}>
                        Descubra la App
                    </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Section>
      </Container>

      <Container maxWidth="lg">
        <Section>
          <Typography
            variant="h4"
            gutterBottom
            fontWeight="light"
            align="center"
            color={darkGraphite}
            mb={2}
          >
            Nuestros Diferenciadores
          </Typography>
          <Typography
            variant="h5"
            gutterBottom
            fontWeight="bold"
            align="center"
            color={goldAccent}
            mb={8}
          >
            Una experiencia por encima de lo ordinario.
          </Typography>

          <Grid container spacing={5}>
            {[
              {
                icon: <CheckCircleOutlineIcon sx={{ fontSize: 'inherit' }} />,
                title: "Diseño de Autor",
                text: "Cada cabina ha sido creada con maderas exóticas y acabados en piedra, priorizando la estética y la ergonomía de lujo.",
              },
              {
                icon: <LockOpenIcon sx={{ fontSize: 'inherit' }} />,
                title: "Total Privacidad",
                text: "Unidades individuales en nuestro pabellón industrial rediseñado. La llave de acceso es digital, directamente en su smartphone.",
              },
              {
                icon: <StarOutlineIcon sx={{ fontSize: 'inherit' }} />,
                title: "Confort Superior",
                text: "Saunas de espectro completo (infrarrojos y finlandesa) con cromoterapia y sistema de sonido integrado controlado por App.",
              },
            ].map(({ icon, title, text }, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <StyledCard sx={{ animation: `${scaleIn} 0.5s ease-out forwards ${0.1 * idx}s` }}>
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: theme.spacing(4) }}>
                    <CardIcon>{icon}</CardIcon>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color={darkGraphite}>
                      {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {text}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section textAlign="center">
          <Paper elevation={0} sx={{ p: isMobile ? 4 : 8, background: darkGraphite, borderRadius: 1, color: offWhite, border: `5px solid ${goldAccent}` }}>
            <Typography
              variant="h3"
              gutterBottom
              fontWeight="bold"
              align="center"
              mb={2}
              sx={{ color: goldAccent }}
            >
              Exclusividad Limitada
            </Typography>
            <Typography
              variant="h6"
              align="center"
              maxWidth="800px"
              mx="auto"
              mb={4}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              Las reservas son limitadas para garantizar una experiencia sin igual. Únase a nuestra lista de acceso prioritario.
            </Typography>
            <Box textAlign="center">
              <CTAButton variant="contained">Unirse a la Lista de Espera VIP</CTAButton>
            </Box>
          </Paper>
        </Section>

      </Container>
    </MainContainer>
  );
};

export default SaunaHavenLanding;